// PropertyPhotosCard — drag-drop upload + gallery grid for a Lead.
//
// Sits on the Lead Report page and calls the v3 photo endpoints:
//   POST   /api/v3/leads/{id}/photos          (upload one file)
//   GET    /api/v3/leads/{id}/photos          (list)
//   DELETE /api/v3/leads/{id}/photos/{photoId}
//
// Upload constraints (mirror backend): 12 MB per file, jpg/jpeg/png/webp/
// heic/heif/gif, cap of 20 photos per lead.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Trash2, Upload, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_MB = 12;
const MAX_PER_LEAD = 20;
const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif";

function bytesToMb(n) { return (n / (1024 * 1024)).toFixed(1); }

export default function PropertyPhotosCard({ leadId, authToken, onChange }) {
  const [photos, setPhotos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver]   = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/leads/${leadId}/photos`);
      setPhotos(data.photos || []);
    } catch (err) {
      // Silent fail on load — new leads or unauthenticated calls.
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`${file.name}: too big (${bytesToMb(file.size)} MB — max ${MAX_MB} MB)`);
      return null;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/v3/leads/${leadId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Upload failed: ${file.name}`);
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList).slice(0, MAX_PER_LEAD - photos.length);
    if (!files.length) {
      toast.error(`Photo cap reached (${MAX_PER_LEAD}). Delete some first.`);
      return;
    }
    setUploading(files.length);
    let ok = 0;
    for (const f of files) {
      const res = await doUpload(f);
      if (res) ok += 1;
      setUploading((n) => Math.max(0, n - 1));
    }
    setUploading(0);
    if (ok > 0) {
      toast.success(`${ok} photo${ok === 1 ? "" : "s"} uploaded`);
      await load();
      onChange && onChange();
    }
  };

  const onDelete = async (photoId) => {
    if (!window.confirm("Delete this photo? This can't be undone.")) return;
    try {
      await api.delete(`/v3/leads/${leadId}/photos/${photoId}`);
      toast.success("Photo deleted");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remaining = Math.max(0, MAX_PER_LEAD - photos.length);

  return (
    <section
      className="print:hidden rounded-2xl border border-slate-200 bg-white shadow-sm p-5 mb-5"
      data-testid="property-photos-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 flex items-center gap-1.5">
            <Camera size={11} /> Property photos
          </div>
          <h3 className="font-bold text-lg mt-0.5 leading-tight">
            {loading ? "Loading…" : `${photos.length} / ${MAX_PER_LEAD}`}
            <span className="ml-2 text-[12px] font-normal text-slate-500">
              JPG / PNG / WEBP / HEIC · up to {MAX_MB} MB each
            </span>
          </h3>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="property-photos-upload-btn"
          disabled={remaining === 0 || uploading > 0}
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-white text-[12.5px] font-bold px-3.5 py-1.5 shadow-sm hover:opacity-90 disabled:opacity-50"
        >
          {uploading > 0 ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading > 0 ? `Uploading ${uploading}…` : "Upload photos"}
        </button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="property-photos-dropzone"
        className={
          "cursor-pointer rounded-xl border-2 border-dashed p-6 transition-all " +
          (dragOver
            ? "border-amber-500 bg-amber-50"
            : "border-slate-200 hover:border-amber-300 hover:bg-amber-50/30")
        }
      >
        {photos.length === 0 && !loading ? (
          <div className="text-center">
            <Camera className="w-8 h-8 mx-auto text-slate-400" />
            <div className="mt-2 text-[13.5px] font-semibold text-slate-700">
              Drop photos here or click to browse
            </div>
            <div className="text-[11.5px] text-slate-500 mt-1">
              Interior + exterior shots make the biggest difference in buyer response rates.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {photos.map((p) => (
              <PhotoTile key={p.id} photo={p} authToken={authToken}
                          onDelete={() => onDelete(p.id)} />
            ))}
            {remaining > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                data-testid="property-photos-add-more"
                className="aspect-video rounded-lg border-2 border-dashed border-slate-300 hover:border-amber-400 grid place-items-center text-slate-400 hover:text-amber-600 transition-colors"
              >
                <div className="text-center">
                  <Upload size={16} className="mx-auto" />
                  <div className="text-[10.5px] font-semibold mt-1">Add more</div>
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        className="hidden"
        data-testid="property-photos-file-input"
      />
    </section>
  );
}

function PhotoTile({ photo, authToken, onDelete }) {
  const [broken, setBroken] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const src = `${base}${photo.url}${authToken ? `?auth=${encodeURIComponent(authToken)}` : ""}`;
  return (
    <div className="relative group aspect-video rounded-lg overflow-hidden bg-slate-100 border border-slate-200"
         data-testid={`property-photo-tile-${photo.id}`}>
      {broken ? (
        <div className="w-full h-full grid place-items-center text-slate-400 text-[10px]">
          Preview failed
        </div>
      ) : (
        <img
          src={src}
          alt={photo.original_filename || "Property photo"}
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
          loading="lazy"
        />
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        data-testid={`property-photo-delete-${photo.id}`}
        title="Delete photo"
        className="absolute top-1 right-1 h-6 w-6 rounded-full grid place-items-center bg-white/95 text-rose-600 shadow opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 size={12} />
      </button>
      {photo.source === "upload" && (
        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-bold">
          UPLOADED
        </span>
      )}
    </div>
  );
}
